import type { Result, Maybe } from '@zaptic-external/ts-monads'
import type { Procedure } from '../../../shared/domain/campaigns/types.no-deps'
import type { FileExtension } from '../../../shared/domain/files/fileExtensions.no-deps'
import type * as Guide from '../../../shared/domain/guides/types.no-deps'
import type { Identifiable } from '../../../shared/lib/typeHelpers.no-deps'
import type { TSimpleUnit } from '../../../shared/domain/units/types.no-deps'
import type { ReportGuideData } from '../../../shared/domain/viewableReports/types.no-deps'

const parentInfoTypes = ['procedure', 'unit'] as const

export type ParentInfoType = (typeof parentInfoTypes)[number]

// General app models
export type AppGuide = FileAppGuide | UrlAppGuide | WorkflowAppGuide | ReportAppGuide
export interface FileAppGuide extends BaseAppGuide, Guide.FileGuide {
    isVideoFileGuide: boolean
    extension: FileExtension
}
export interface UrlAppGuide extends BaseAppGuide, Guide.UrlGuide {}
export interface WorkflowAppGuide extends BaseAppGuide, Guide.WorkflowGuide {}
export interface ReportAppGuide extends BaseAppGuide, Guide.ReportGuide {}

export interface BaseAppGuide extends Guide.SyncGuideDetails {
    iconUrl: string
    parentInfo?: ParentInfo[]
    version?: number
}

export type ParentInfo = ProcedureParentInfo | UnitParentInfo

export interface ProcedureParentInfo extends BaseParentInfo<'procedure'> {
    procedure: Procedure
}

export interface UnitParentInfo extends BaseParentInfo<'unit'> {
    unit: Identifiable<TSimpleUnit>
}

export interface BaseParentInfo<T extends ParentInfoType> {
    type: T
    name: string
    id: number
}

export type FileGuideDownloadUrlError = 'API'

export type FileGuideDownloadUrl = Result<FileGuideDownloadUrlError, Maybe<string>>

export interface AppFileGuideDetails {
    downloadUrl: FileGuideDownloadUrl
    fileExtension: FileExtension
}

// Guide execution models
interface BaseGuideExecution<V extends Guide.Variant> {
    guide: Guide.SyncGuide<V>
    parentInfo: ParentInfo[]
    variant: V
}

export interface FileGuideExecution extends BaseGuideExecution<'file'> {
    customData: AppFileGuideDetails
}

export interface ReportGuideExecution extends BaseGuideExecution<'report'> {
    customData: ReportGuideData
}

export interface UrlGuideExecution extends BaseGuideExecution<'url'> {}

export interface WorkflowGuideExecution extends BaseGuideExecution<'workflow'> {}

export type GuideExecution = FileGuideExecution | ReportGuideExecution | UrlGuideExecution | WorkflowGuideExecution

// Utility functions
export function isProcedureParentInfo(parentInfo: ParentInfo): parentInfo is ProcedureParentInfo {
    return parentInfo.type === 'procedure'
}

export function isUnitParentInfo(parentInfo: ParentInfo): parentInfo is UnitParentInfo {
    return parentInfo.type === 'unit'
}
